// src/features/engine/ProgramEngine.jsx

import { useSearchParams } from "react-router-dom";
import {
  ApplicationButton,
  ApplicationPage,
  ApplicationSurface
} from "../../components/application";
import { ProgramEngineProvider } from "./context/ProgramEngineContext";
import { useProgramEngine } from "./context/ProgramEngineContext";

import flowComponentRegistry from "./flowComponentRegistry";
import {
  resolveCurrentStep
} from "./utils/stepUtils";

// =======================================================
// INNER ENGINE
// =======================================================

function ProgramEngineInner() {

  const {
    state,
    program,
    programState,
    projectMode,
    projectUIFlow,
    completeFirstOpenPresentation
  } = useProgramEngine();

  const [searchParams] = useSearchParams();
  const forcedStep = searchParams.get("step");

  const resolvedSteps = program?.steps || [];

  const mode = projectMode || "dashboard";

  if (programState?.firstOpenPresentation?.status === "presentation_required") {
    const presentation = programState.firstOpenPresentation;
    return <ApplicationPage
      className="av-app-program-presentation"
      variant="workspace"
      width="reading"
    >
      <ApplicationSurface
        aria-label="Program introduction"
        className="av-app-program-presentation__surface"
        elevation="raised"
      >
        <div className="av-app-program-presentation__copy">
          {presentation.text.split("\n\n").map(paragraph => <p key={paragraph}>{paragraph}</p>)}
        </div>
        <div className="av-app-program-presentation__actions">
          <ApplicationButton onClick={completeFirstOpenPresentation}>
            Continue
          </ApplicationButton>
        </div>
      </ApplicationSurface>
    </ApplicationPage>;
  }

  // =======================================================
  // 🔵 DASHBOARD MODE (IGNORA FLOW COMPLETAMENT)
  // =======================================================

  if (mode === "dashboard") {

    const currentStepWithState = resolveCurrentStep({
      mode,
      forcedStep,
      programState,
      resolvedSteps
    });

    if (!currentStepWithState) {
      console.error("❌ Step not found or invalid", programState);
      return <div>Error: invalid step</div>;
    }

    // 👉 sempre TaskExecutor en dashboard
    const TaskExecutor = flowComponentRegistry["TaskExecutor"];

    if (!TaskExecutor) {
      console.error("❌ TaskExecutor not found in registry");
      return <div>Error: TaskExecutor missing</div>;
    }

    return <TaskExecutor key={currentStepWithState.key} step={currentStepWithState} />;
  }

  // =======================================================
  // 🟣 FLOW MODE (només aquí usem uiFlow)
  // =======================================================

  const flow = projectUIFlow || {};

  const stateToComponent = {
    intro: flow.startComponent,
    running: flow.taskComponent,
    completion: flow.completionComponent,
    results: flow.resultsComponent
  };

  const componentKey = stateToComponent[state];
  const Component = flowComponentRegistry[componentKey];

  if (!Component) {
    console.error("❌ Component not found:", componentKey);
    return <div>Component not found: {componentKey}</div>;
  }

  // -------------------------------------------------------
  // RUNNING
  // -------------------------------------------------------

  if (state === "running") {

    const currentStepWithState = resolveCurrentStep({
      mode,
      forcedStep,
      programState,
      resolvedSteps
    });

    if (!currentStepWithState) {
      return <div>Error: invalid step</div>;
    }

    return <Component key={currentStepWithState.key} step={currentStepWithState} />;
  }

  // -------------------------------------------------------
  // INTRO / COMPLETION / RESULTS
  // -------------------------------------------------------

  return <Component />;
}

// =======================================================
// INITIAL STATE
// =======================================================

function resolveInitialUIState(programState) {

  if (!programState) return "intro";

  if (programState.isProgramCompleted) {
    return "completion";
  }

  if (programState.currentStep) {
    return "running";
  }

  return "intro";
}

// =======================================================
// MAIN
// =======================================================

export default function ProgramEngine({
  program,
  attempt,
  programState,
  apiUrl,
  token,
  projectMode,
  projectUIFlow
}) {

  if (!program) {
    return <div>Loading...</div>;
  }

  const initialUIState =
    resolveInitialUIState(programState);

  return (
    <ProgramEngineProvider
      program={program}
      attempt={attempt}
      programState={programState}
      apiUrl={apiUrl}
      token={token}
      projectMode={projectMode}
      projectUIFlow={projectUIFlow}
      initialUIState={initialUIState}
    >
      <ProgramEngineInner />
    </ProgramEngineProvider>
  );
}
